/**
 * REGTA-49 — link review-readiness entries to their editor controls.
 *
 * Editor surfaces tag their controls with `data-locate-anchor="kind:id"`;
 * the StatusBar's missing-entry links call into here to highlight
 * (hover) or scroll-and-flash (click) the matching element. Callers pass
 * a candidate list so an entry can fall back — e.g. a party-autofilled
 * field has no control until its slot is filled, so the slot card is the
 * second candidate.
 */

export type LocateKind = 'field' | 'slot' | 'attachment_slot';

export const locateAnchor = (kind: LocateKind, id: string): string =>
  `${kind}:${id}`;

const query = (anchor: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `[data-locate-anchor="${CSS.escape(anchor)}"]`,
  );

const findFirst = (anchors: ReadonlyArray<string>): HTMLElement | null => {
  for (const anchor of anchors) {
    const el = query(anchor);
    if (el) return el;
  }
  return null;
};

export const setLocateHover = (
  anchors: ReadonlyArray<string>,
  on: boolean,
): void => {
  if (!on) {
    // Clear every candidate — the DOM may have changed since mouseenter.
    for (const anchor of anchors) {
      query(anchor)?.classList.remove('regta-locate-hover');
    }
    return;
  }
  findFirst(anchors)?.classList.add('regta-locate-hover');
};

export const scrollToLocateAnchor = (
  anchors: ReadonlyArray<string>,
): void => {
  let tries = 0;
  // Retry across a few frames — the target may only render after a tab
  // switch triggered by the same click.
  const attempt = (): void => {
    const el = findFirst(anchors);
    if (!el) {
      if (++tries < 40) requestAnimationFrame(attempt);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('regta-locate-hover', 'regta-locate-flash');
    void el.offsetWidth;
    el.classList.add('regta-locate-flash');
    window.setTimeout(() => el.classList.remove('regta-locate-flash'), 2000);
    if (el.dataset.locateAnchor?.startsWith('field:')) {
      el.querySelector<HTMLElement>('input, select, textarea')?.focus({
        preventScroll: true,
      });
    }
  };
  attempt();
};
